import {
  Fragment,
  type ComponentProps,
  type ComponentType,
  type MouseEvent,
  type ReactNode,
  useState,
} from "react";
import List from "@mui/material/List";
import ListItemBase from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Checkbox from "@mui/material/Checkbox";
import { Button, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";

import { InfoOutlined } from "@mui/icons-material";
import RpMUIDialog from "@/components/modals/RpMUIModal";

type CreativeOptionValue = string | number;

type CreativeOption = {
  label: ReactNode;
  value: CreativeOptionValue;
};

type CreativeOptionsProps = {
  creativesOptions?: CreativeOption[];
  initiallySelected?: CreativeOptionValue[];
  currentInView?: CreativeOptionValue;
  onClickView?: (_value: CreativeOptionValue) => void;
  onSelect?: (_selectedCreatives: CreativeOptionValue[]) => void;
  creative_select_flag?: boolean;
  creative_select_max_value?: number;
  initialDisabled?: boolean;
};

type StyledListItemProps = {
  isInPreview?: boolean;
  isSelected?: boolean;
};

const ListItem = ListItemBase as unknown as ComponentType<
  ComponentProps<typeof ListItemBase> & {
    button?: boolean;
  } & StyledListItemProps
>;

const StyledList = styled(List)(({ theme }) => ({
  width: "100%",
  backgroundColor: theme.palette.background.paper,
  "& .MuiListItemIcon-root": {
    minWidth: "36px",
  },
}));

const StyledListItem = styled(ListItem)<StyledListItemProps>(
  ({ theme, isInPreview, isSelected }) => ({
    paddingRight: 1,
    marginRight: 3,
    border:
      isInPreview || isSelected
        ? "1px solid var(--primary-base)"
        : "1px solid var(--neutral-200)",
    borderRadius: "var(--radius-control)",
    flex: 1,
    ...(isSelected && {
      background: `${theme?.palette?.primary?.main || "var(--primary-base)"} !important`,
      color: "var(--palette-primary-contrastText) !important",
    }),
    ...(isInPreview &&
      !isSelected && {
        background: "var(--primary-tint) !important",
        color: "var(--palette-text-primary) !important",
      }),
  })
);

export default function CreativeOptions({
  creativesOptions = [],
  initiallySelected = [],
  currentInView,
  onClickView,
  onSelect,
  creative_select_flag,
  creative_select_max_value,
  initialDisabled,
}: CreativeOptionsProps) {
  const [checked, setChecked] = useState(initiallySelected);
  const [showMaxMsg, setShowMaxMsg] = useState(false);

  const isMaxSelected = () => {
    if (creative_select_flag && checked?.length === creative_select_max_value) {
      return true;
    }
    return false;
  };

  const handleSelectCreative = (
    v: CreativeOptionValue,
    e: MouseEvent<HTMLButtonElement>
  ) => {
    e.stopPropagation(); // Prevent triggering preview when clicking checkbox
    let newChecked = [...checked];
    if (creative_select_flag) {
      // for multi select
      newChecked = [...checked];
      const idx = checked.indexOf(v);
      if (idx === -1) {
        if (isMaxSelected()) {
          setShowMaxMsg(true);
          return;
        }
        newChecked.push(v);
        setChecked(newChecked);
      } else {
        newChecked.splice(idx, 1);
        setChecked(newChecked);
      }
    } else {
      // for single select
      newChecked = [v];
      const idx = checked.indexOf(v);
      if (idx !== -1) {
        newChecked = [];
      }
      setChecked(newChecked);
    }
    // update back
    onSelect?.(newChecked);
  };

  const handleNameClick = (v: CreativeOptionValue) => {
    onClickView?.(v); // Show preview when clicking the name
  };

  const isSelectedOption = (v: CreativeOptionValue) => {
    return checked.length ? checked.indexOf(v) !== -1 : false;
  };

  return (
    <div className="creative-options mr-4">
      {/* <div className="pl-3 ml-1 py-0 my-2"></div> */}
      {/* <Typography component="p" variant="body2">
        {`Select ${
          creative_select_flag ? `up to ${creative_select_max_value}` : "One"
        }`}
      </Typography> */}
      <StyledList>
        {creativesOptions.map((c) => {
          const isSelected = isSelectedOption(c.value);
          const isPreview = currentInView === c.value;
          return (
            <Fragment key={c.value}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: 5,
                  backgroundColor: isSelected
                    ? "var(--primary-tint)"
                    : "transparent",
                  borderRadius: "var(--radius-container)",
                  padding: "8px",
                  marginRight: 5,
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: "36px",
                    marginRight: 0,
                    borderRadius: "var(--radius-control)",
                  }}
                >
                  <Checkbox
                    edge="start"
                    checked={isSelected}
                    tabIndex={-1}
                    disableRipple
                    onClick={(e) => {
                      if (initialDisabled) {
                        return;
                      }
                      handleSelectCreative(c.value, e);
                    }}
                    inputProps={{
                      "aria-labelledby": `checkbox-list-label-${c.value}`,
                    }}
                    disabled={initialDisabled}
                    sx={{
                      color: "var(--primary-base)",
                      "&.Mui-checked": {
                        color: "var(--primary-base)",
                      },
                    }}
                  />
                </ListItemIcon>
                <StyledListItem
                  dense
                  button
                  onClick={() => {
                    handleNameClick(c.value);
                  }}
                  isInPreview={isPreview}
                  isSelected={isSelected}
                >
                  <ListItemText
                    id={`checkbox-list-label-${c.value}`}
                    primary={c.label}
                    className="creative-option-label"
                    sx={{
                      fontWeight: isPreview ? "bolder" : "normal",
                      textDecoration: "none",
                    }}
                  />
                </StyledListItem>
              </div>
            </Fragment>
          );
        })}
      </StyledList>

      <RpMUIDialog
        open={showMaxMsg}
        onClose={() => setShowMaxMsg(false)}
        maxWidth="xs"
        persists
      >
        <div className="px-3">
          <div className="text-center mb-2">
            <InfoOutlined color="error" fontSize="large" />
          </div>
          <Typography>
            You've selected the max amount of creatives, to select another
            please unselect one of your currently selected creatives.
          </Typography>
          <div className="text-center mt-3">
            <Button
              variant="contained"
              color="primary"
              onClick={() => setShowMaxMsg(false)}
            >
              OK
            </Button>
          </div>
        </div>
      </RpMUIDialog>
    </div>
  );
}
